// src/services/deviceInbox.js — the copies the phone left behind.
//
// 🟥 THE BROWSER USED TO ASK THE PHONE, AND THE PHONE HAD TO BE AWAKE. Store-and-
//    forward destroys a message document ~50ms after the phone caches it, so
//    everything that arrived while this tab was closed was already gone by the
//    time it opened. The only surviving copy was in the phone's SQLite, and the
//    only way to get it was a request document the phone answered while running
//    — which on iOS the operating system will not wake it to do.
//
// 🟢 THE PHONE NOW LEAVES A COPY INSTEAD OF ANSWERING QUESTIONS. Before it
//    sweeps, it writes each doomed document into this device's mailbox. Draining
//    it needs nothing running anywhere: phone asleep, phone dead, phone in a
//    drawer, the messages are already here.
//
// 🟢 AND THE COPY IS THE DOCUMENT ITSELF. Every message was already sealed
//    per-device to this browser the moment it linked, so this is a row it could
//    always have read — it simply was never awake while it existed. Nothing here
//    decrypts; the ordinary decoder does that, exactly as it does for a live doc.
import {
  collection, getDocs, query, orderBy, limit, writeBatch, doc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getIdentity } from '../crypto/identity';
import { mailboxExpired } from './syncFanout';

/* One drain per open reads at most this many rows. A tab that has been shut for a
   month has a bounded first paint; the rest arrives on the next drain. */
const DRAIN_MAX = 500;

/**
 * Read everything waiting for this browser, grouped by chat.
 *
 * 🟥 READ THEN DELETE, AND NOT IN ONE STEP. An entry removed before the caller
 *    has filed it is an entry lost for good — the phone has already swept the
 *    original. `drain` hands the rows back and `clear` is called only once they
 *    are in the caller's store.
 */
export async function drain(uid) {
  const id = await getIdentity();
  if (!uid || !id?.deviceId) return { byChat: new Map(), keys: [] };
  const col = collection(db, 'users', uid, 'device_inbox', id.deviceId, 'messages');
  let snap;
  try {
    snap = await getDocs(query(col, orderBy('at', 'asc'), limit(DRAIN_MAX)));
  } catch {
    /* 🟢 A MAILBOX THAT WILL NOT READ IS NOT AN EMPTY ONE. The live listeners
       below still run; the browser is behind, not broken. */
    return { byChat: new Map(), keys: [] };
  }

  const byChat = new Map();
  const keys = [];
  const now = Date.now();
  for (const d of snap.docs) {
    const e = d.data() || {};
    keys.push(d.id);
    const at = e.at?.toMillis ? e.at.toMillis() : Number(e.at) || 0;
    /* 🟢 AN EXPIRED ENTRY IS STILL COLLECTED FOR DELETION, just not rendered —
       the same rule the phone applies, so neither end keeps what the other has
       written off. */
    if (mailboxExpired({ at }, { now })) continue;
    if (!e.chatId || !e.row) continue;
    const rows = byChat.get(e.chatId) || [];
    rows.push({ id: e.messageId || d.id, ...e.row });
    byChat.set(e.chatId, rows);
  }
  return { byChat, keys };
}

/** Drop what has been filed. Safe to call with nothing. */
export async function clear(uid, keys) {
  const id = await getIdentity();
  if (!uid || !id?.deviceId || !keys?.length) return;
  const col = collection(db, 'users', uid, 'device_inbox', id.deviceId, 'messages');
  for (let i = 0; i < keys.length; i += 400) {
    const batch = writeBatch(db);
    for (const k of keys.slice(i, i + 400)) batch.delete(doc(col, k));
    try { await batch.commit(); } catch { /* it will be re-drained and re-dropped */ }
  }
}

/** Drain and file in one call, for a caller that has somewhere to put them. */
export async function drainInto(uid, file) {
  const { byChat, keys } = await drain(uid);
  if (!keys.length) return 0;
  let n = 0;
  for (const [chatId, rows] of byChat) { await file(chatId, rows); n += rows.length; }
  await clear(uid, keys);
  return n;
}
